'use client';

import { IAuthorResponse, IPostInfo, IPostsListResponse } from '@sapientpro/sapientpro-data-models';
import classnames from 'classnames';
import { useAtomValue } from 'jotai';
import Link from 'next/link';
import React, { useState } from 'react';
import { MouseParallax } from 'react-just-parallax';
import client from '../../../apiClient';
import Button, { ButtonVariants } from '../../../components/Button';
import Ellipse, { EllipseVariants } from '../../../components/Ellipse';
import PostFilters from '../../../components/PostFilters';
import deviceScreenSize, { ScreenSize } from '../../../store/deviceScreenSize';
import { theme, Theme } from '../../../store/theme';
import ArticleCard from '../ArticleCard';
import AuthorCard from '../AuthorCard';
import Chip from '../Chip';
import styles from './Author.module.scss';
import Img from '../../../components/Img';

type AuthorProps = {
  data: IAuthorResponse,
  posts: IPostsListResponse,
  limit: number,
};

type GetAuthorPostsParams = {
  offset?: number,
  limit?: number,
  category?: string,
};

const getAuthorPosts = async (author: string, params: { searchParams: GetAuthorPostsParams }) => {
  try {
    return await client.get<IPostsListResponse>(
      `authors/${author}/posts?${new URLSearchParams(JSON.parse(JSON.stringify(params.searchParams)))}`,
    );
  } catch (e) {
    console.error(e.message);
  }
};

const Author = ({ data, posts, limit }: AuthorProps) => {
  const deviceSize = useAtomValue(deviceScreenSize);
  const appTheme = useAtomValue<Theme>(theme);
  const [isLoading, setIsLoading] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const [total, setTotal] = useState<number | null>(posts.pagination.total);
  const [filteredCategory, setFilteredCategory] = useState<string | undefined>('date');
  const [renderedPosts, setRenderedPosts] = useState<IPostInfo[]>(posts.posts);

  const loadPosts = async (params = {}, clearOffset = false) => {
    try {
      setIsLoading(true);
      const loadedPosts = await getAuthorPosts(data.author.slug, {
        searchParams: JSON.parse(JSON.stringify({
          offset: clearOffset ? 0 : offset + limit,
          limit,
          order: filteredCategory,
          ...params,
        })),
      });
      setOffset(clearOffset ? 0 : offset + limit);
      setTotal(loadedPosts?.pagination?.total || null);
      setRenderedPosts(prev => (clearOffset ? (loadedPosts?.posts || []) : [...prev, ...(loadedPosts?.posts || [])]));
    } catch (e) {
      console.error(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const setEllipseSizes = (
    mobile: number,
    portrait: number,
    landscape: number,
    desktopSmall: number,
    desktop: number,
    desktopBig: number,
    desktopHuge: number,
  ): number => (
    deviceSize === ScreenSize.TABLET_PORTRAIT ? portrait
      : deviceSize === ScreenSize.TABLET_LANDSCAPE ? landscape
        : deviceSize === ScreenSize.DESKTOP_SMALL ? desktopSmall
          : deviceSize === ScreenSize.DESKTOP ? desktop
            : deviceSize === ScreenSize.DESKTOP_BIG ? desktopBig
              : deviceSize === ScreenSize.DESKTOP_HUGE ? desktopHuge
                : mobile
  );

  return (
    <main className={styles.main}>
      <div className={styles.images}>
        <MouseParallax
          shouldPause={false}
          isAbsolutelyPositioned
          strength={0.2}
        >
          <Ellipse
            variant={EllipseVariants.NOISE}
            className={classnames(styles.ellipse, styles.ellipse_noise)}
            size={setEllipseSizes(140, 159, 92, 132, 132, 159, 187)}
          />
        </MouseParallax>
        <MouseParallax
          shouldPause={false}
          isAbsolutelyPositioned
          strength={0.3}
        >
          <Ellipse
            variant={EllipseVariants.GRADIENT}
            className={classnames(styles.ellipse, styles.ellipse_gradient)}
            size={setEllipseSizes(28, 60, 34, 44, 50, 59, 93)}
          />
        </MouseParallax>
        <MouseParallax
          shouldPause={false}
          isAbsolutelyPositioned
          strength={0.1}
        >
          <Ellipse
            variant={EllipseVariants.BORDER}
            size={setEllipseSizes(11, 24, 14, 18, 20, 24, 40)}
            className={classnames(styles.ellipse, styles.ellipse_border)}
          />
        </MouseParallax>
        <MouseParallax
          shouldPause={false}
          isAbsolutelyPositioned
          strength={0.04}
        >
          <Ellipse
            variant={EllipseVariants.DRAW}
            size={setEllipseSizes(421, 894, 510, 657, 740, 892, 1400)}
            className={classnames(styles.ellipse, styles.ellipse_draw)}
          />
        </MouseParallax>
      </div>
      <section className={styles.hero}>
        <div className='container'>
          <div className={styles.author}>
            <div className={styles.author__mainInfo}>
              <div className={styles.avatar}>
                <Img
                  src={data.author.avatar?.url || '/'}
                  alt={data.author.avatar?.alternativeText || ''}
                  withPreloader
                />
              </div>
              <h1 className={styles.name}>{data.author.pseudonym}</h1>
              <div className={styles.achievements}>
                <p className={styles.achievements__item}>
                  <Img
                    src='/media/file.svg'
                    width={20}
                    height={20}
                    alt=''
                  />
                  {data.author.numberOfPosts}
                  {' '}
                  publications
                </p>
              </div>
            </div>
            <p className={styles.description}>
              {data.author.description}
            </p>
          </div>
          <div className={styles.chips}>
            {data.tags.map(chip => (
              <Chip
                url={`/blog?tag=${chip.slug}`}
                title={chip.title}
                key={chip.slug}
              />
            ))}
          </div>
        </div>
      </section>
      <section className={classnames(styles.authorContent, 'container')}>
        <section className={styles.articles}>
          <h2 className={classnames('subtitle', 'big', styles.articlesTitle)}>Author articles:</h2>
          <PostFilters
            className={styles.categories}
            filters={[{
              id: 0,
              title: 'Newest',
              slug: 'date',
            }, {
              id: 1,
              title: 'Most Viewed',
              slug: 'views',
            }]}
            activeFilter={filteredCategory}
            callback={(filterSlug) => {
              loadPosts({ order: filterSlug }, true);
              setFilteredCategory(filterSlug);
            }}
          />
          <div className={styles.articles__content}>
            {renderedPosts.map(article => (
              <ArticleCard
                className={styles.articleCard}
                data={article}
                key={article.id}
              />
            ))}
          </div>
          {(total === null || total > renderedPosts.length) && (
            <Button
              variant={ButtonVariants.OUTLINED}
              className={styles.loadMoreButton}
              iconSize={{ width: 24, height: 24 }}
              onClick={() => loadPosts()}
              icon={isLoading && (
                <img
                  src={`/media/spinner_${appTheme}.webp`}
                  alt=''
                />
              )}
            >
              Load more
            </Button>
          )}
        </section>
        <aside className={styles.sidebar}>
          <section className={styles.trendingArticles}>
            <h3 className={classnames('subtitle', styles.trendingTitle)}>Trending articles:</h3>
            <div className={styles.trendingContent}>
              {data.trendingPosts.map(article => (
                <ArticleCard
                  className={styles.trendingArticleCard}
                  data={article}
                  key={article.id}
                  withoutDescription
                  withoutCategory
                  verticalLayout={deviceSize <= ScreenSize.TABLET_LANDSCAPE}
                  minifiedLayout={deviceSize > ScreenSize.TABLET_LANDSCAPE}
                />
              ))}
            </div>
          </section>
          {data.othersAuthors.length > 0 && (
            <section className={styles.otherAuthors}>
              <h3 className={classnames('subtitle', styles.trendingTitle)}>Other authors:</h3>
              <div className={styles.otherAuthorsContent}>
                {data.othersAuthors.map(otherAuthor => (
                  <Link
                    href={otherAuthor.slug}
                    className={styles.authorCard}
                    key={otherAuthor.id}
                  >
                    <AuthorCard
                      avatar={otherAuthor.avatar?.url || '/'}
                      name={otherAuthor.pseudonym}
                      info={otherAuthor.position}
                    />
                    <div className={styles.authorCard__linkIcon}>
                      <svg>
                        <use
                          width={20}
                          height={20}
                          xlinkHref='/media/icon-nest.svg#iconNest'
                          href='/media/icon-nest.svg#iconNest'
                        />
                      </svg>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
          {/* <FollowBlock /> */}
        </aside>
      </section>
    </main>
  );
};

export default Author;
